import { motion } from "framer-motion";
import { Heart, Users, Calendar, Gift, Star, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IMAGES } from "@/assets/images";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { useState } from "react";

const eventServices = [
  {
    id: "wedding",
    icon: Heart,
    titleAr: "حفلات الزفاف",
    descriptionAr: "نجعل يومك الخاص لا يُنسى بأطباق فاخرة وتقديم احترافي يليق بأجمل لحظاتك",
  },
  {
    id: "engagement",
    icon: Gift,
    titleAr: "حفلات الخطوبة",
    descriptionAr: "ضيافة راقية لمناسبتك السعيدة مع تشكيلة متنوعة من الحلويات والمخبوزات الفاخرة",
  },
  {
    id: "corporate",
    icon: Users,
    titleAr: "الفعاليات الكبرى",
    descriptionAr: "خدمة كيترنج احترافية للمؤتمرات والفعاليات الكبيرة بكميات ضخمة وجودة ثابتة",
  },
  {
    id: "graduation",
    icon: Calendar,
    titleAr: "حفلات التخرج",
    descriptionAr: "احتفل بإنجازك مع ضيافة مميزة وبوكسات أنيقة تناسب أعداد الضيوف الكبيرة",
  },
];

const whyUsReasons = [
  {
    id: "quality",
    titleAr: "جودة استثنائية",
    descriptionAr: "نستخدم أجود المكونات ونلتزم بأعلى معايير الجودة في كل طبق",
  },
  {
    id: "experience",
    titleAr: "خبرة واسعة",
    descriptionAr: "سنوات من الخبرة في تقديم خدمات الكيترنج للمناسبات الكبرى",
  },
  {
    id: "customization",
    titleAr: "تخصيص كامل",
    descriptionAr: "نصمم قوائم خاصة تناسب ذوقك وميزانيتك وعدد ضيوفك",
  },
  {
    id: "timing",
    titleAr: "التزام بالمواعيد",
    descriptionAr: "نضمن وصول الطلبات في الوقت المحدد بدقة تامة",
  },
];

const testimonial = {
  nameAr: "سارة العتيبي",
  eventAr: "حفل زفاف - الرياض",
  rating: 5,
  quoteAr: "كان حفل زفافي مثاليًا بفضل أطياف الخبازين. الطعام كان رائعًا والتقديم كان احترافيًا جدًا. جميع الضيوف أشادوا بجودة الحلويات والمخبوزات. شكرًا لكم على جعل يومي الخاص لا يُنسى!",
};

export default function WeddingsEvents() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    eventType: "none",
    eventDate: "",
    guestCount: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
        <section className="relative h-[70vh] lg:h-[80vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.WEDDING_CATERING_1}
              alt="حفلات الزفاف والمناسبات"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="relative z-10 container mx-auto px-4 text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Playfair Display, Cairo, serif" }}
            >
              نجعل مناسبتك لا تُنسى
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8"
            >
              خدمات كيترنج فاخرة لحفلات الزفاف والمناسبات الخاصة
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-300 text-lg px-8 py-6"
                onClick={() => document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" })}
              >
                احجز موعد استشارة
              </Button>
            </motion.div>
          </motion.div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
                style={{ fontFamily: "Cairo, sans-serif" }}
              >
                خدماتنا للمناسبات
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-xl text-muted-foreground max-w-2xl mx-auto">
                نقدم حلول كيترنج متكاملة لجميع أنواع المناسبات
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {eventServices.map((service) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={service.id}
                    variants={staggerItem}
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={springPresets.snappy}
                    className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:border-accent transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: "Cairo, sans-serif" }}>
                      {service.titleAr}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{service.descriptionAr}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
                style={{ fontFamily: "Cairo, sans-serif" }}
              >
                معرض أعمالنا
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-xl text-muted-foreground">
                لحظات لا تُنسى صنعناها لعملائنا
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {[
                IMAGES.WEDDING_CATERING_2,
                IMAGES.WEDDING_CATERING_4,
                IMAGES.WEDDING_CATERING_5,
                IMAGES.WEDDING_CATERING_6,
                IMAGES.WEDDING_CATERING_7,
                IMAGES.WEDDING_CATERING_9,
                IMAGES.HOSPITALITY_TABLE_3,
                IMAGES.HOSPITALITY_TABLE_7,
              ].map((image, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ scale: 1.05 }}
                  transition={springPresets.snappy}
                  className="relative aspect-square rounded-xl overflow-hidden shadow-md group cursor-pointer"
                >
                  <img src={image} alt={`معرض ${index + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
                style={{ fontFamily: "Cairo, sans-serif" }}
              >
                لماذا تختارنا؟
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {whyUsReasons.map((reason, index) => (
                <motion.div
                  key={reason.id}
                  variants={staggerItem}
                  className="bg-card rounded-2xl p-8 shadow-sm border border-border"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-4 text-accent font-bold text-xl">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3" style={{ fontFamily: "Cairo, sans-serif" }}>
                    {reason.titleAr}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{reason.descriptionAr}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-card rounded-3xl p-8 lg:p-12 shadow-lg border border-border">
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-xl lg:text-2xl text-foreground leading-relaxed mb-8 italic">
                  "{testimonial.quoteAr}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                    <Heart className="w-8 h-8 text-accent" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground" style={{ fontFamily: "Cairo, sans-serif" }}>
                      {testimonial.nameAr}
                    </p>
                    <p className="text-muted-foreground">{testimonial.eventAr}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="quote-form" className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="max-w-3xl mx-auto"
            >
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2
                  className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
                  style={{ fontFamily: "Cairo, sans-serif" }}
                >
                  احجز استشارة مجانية
                </h2>
                <p className="text-xl text-muted-foreground">
                  دعنا نساعدك في التخطيط لمناسبتك المثالية
                </p>
              </motion.div>

              <motion.form
                variants={fadeInUp}
                onSubmit={handleSubmit}
                className="bg-card rounded-3xl p-8 lg:p-12 shadow-lg border border-border space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-foreground text-lg">
                      الاسم الكامل
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 text-lg"
                      placeholder="أدخل اسمك"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground text-lg">
                      رقم الجوال
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12 text-lg"
                      placeholder="05xxxxxxxx"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="eventType" className="text-foreground text-lg">
                      نوع المناسبة
                    </Label>
                    <Select
                      value={formData.eventType}
                      onValueChange={(value) => setFormData({ ...formData, eventType: value })}
                    >
                      <SelectTrigger className="h-12 text-lg">
                        <SelectValue placeholder="اختر نوع المناسبة" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">اختر نوع المناسبة</SelectItem>
                        <SelectItem value="wedding">حفل زفاف</SelectItem>
                        <SelectItem value="engagement">حفل خطوبة</SelectItem>
                        <SelectItem value="corporate">فعالية كبرى</SelectItem>
                        <SelectItem value="graduation">حفل تخرج</SelectItem>
                        <SelectItem value="other">مناسبة أخرى</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventDate" className="text-foreground text-lg">
                      تاريخ المناسبة
                    </Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                      className="h-12 text-lg"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guestCount" className="text-foreground text-lg">
                    عدد الضيوف المتوقع
                  </Label>
                  <Input
                    id="guestCount"
                    type="number"
                    value={formData.guestCount}
                    onChange={(e) => setFormData({ ...formData, guestCount: e.target.value })}
                    className="h-12 text-lg"
                    placeholder="مثال: 200"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground text-lg">
                    تفاصيل إضافية
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="min-h-32 text-lg"
                    placeholder="أخبرنا المزيد عن مناسبتك واحتياجاتك..."
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-300 text-xl py-6"
                >
                  <CheckCircle2 className="ml-2 w-6 h-6" />
                  إرسال الطلب
                </Button>
              </motion.form>
            </motion.div>
          </div>
        </section>
      </div>
  );
}
